import GoogleAnalyticsV2 from "../utils/GoogleAnalyticsV2";
import { GA4EventName, GA4EventParams } from "../types/analytics";

/** Page performance tracking interface */
interface PagePerformanceData {
  path: string;
  loadTime: number;
  renderTime: number;
  timestamp: number;
}

/**
 * AnalyticsServiceV2 - Refactored analytics service with GA4 parameter support
 *
 * This service wraps GoogleAnalyticsV2 and adds:
 * - Performance data tracking and storage
 * - Debug mode with console logging
 * - Page load timing helpers
 *
 * Unlike the old AnalyticsService, this version properly passes all event
 * parameters to GA4 instead of discarding label and value fields.
 */
class AnalyticsServiceV2 {
  private pageLoadStartTime: number = 0;
  private pagePerformanceData: PagePerformanceData[] = [];
  private debugMode: boolean = false;

  /**
   * Check if debug mode is enabled
   */
  private checkDebugMode(): boolean {
    if (typeof window === "undefined") return false;

    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("debug_mode") || urlParams.has("debug")) {
      return true;
    }

    // Check localStorage
    const localStorageDebug = localStorage.getItem("ga_debug_mode");
    if (localStorageDebug === "true") {
      return true;
    }

    // Check environment variable (for development)
    if (process.env.NODE_ENV === "development") {
      return true;
    }

    return false;
  }

  /**
   * Enable or disable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    if (typeof window !== "undefined") {
      localStorage.setItem("ga_debug_mode", enabled.toString());
    }
    console.log(`🔍 Analytics debug mode ${enabled ? "ENABLED" : "DISABLED"}`);
  }

  /**
   * Track a GA4 event with structured parameters.
   *
   * @param eventName - The GA4 event name (use GA4EventName enum)
   * @param params - Event parameters as key-value pairs
   *
   * @example
   * analyticsV2.trackEvent(GA4EventName.HeaderClick, {
   *   action: "my-data",
   *   country_code: "US",
   *   source: "gnb",
   * });
   */
  trackEvent(
    eventName: GA4EventName | string,
    params: GA4EventParams = {}
  ): void {
    GoogleAnalyticsV2.trackEvent(eventName, params);

    if (this.debugMode || this.checkDebugMode()) {
      console.log(`📊 ANALYTICS: Event Tracked`);
      console.log(`📍 Event: ${eventName}`);
      console.log(`📋 Params:`, params);
    }
  }

  /**
   * Track page view with loading time metrics.
   * Sends both a page_view and page_load event to GA4.
   *
   * @param path - The page path
   * @param loadTime - Page load time in milliseconds
   * @param renderTime - Page render time in milliseconds
   */
  trackPageView(path: string, loadTime?: number, renderTime?: number): void {
    const performanceData: PagePerformanceData = {
      path,
      loadTime: loadTime || 0,
      renderTime: renderTime || 0,
      timestamp: Date.now(),
    };

    // Store performance data
    this.pagePerformanceData.push(performanceData);

    // Send page view to GA4
    GoogleAnalyticsV2.sendPageView(path);

    // Send page load event with timing parameters
    if (loadTime) {
      GoogleAnalyticsV2.trackPageLoad(path, loadTime, renderTime);
    }

    // Enhanced logging
    if (this.debugMode || this.checkDebugMode()) {
      console.log(`📊 ANALYTICS: Page View Tracked`);
      console.log(`📍 Path: ${path}`);
      console.log(`⏱️  Load Time: ${Math.round(loadTime || 0)}ms`);
      console.log(`🎨 Render Time: ${Math.round(renderTime || 0)}ms`);
    }
  }

  /**
   * Start timing page load
   */
  startPageLoadTiming(): void {
    this.pageLoadStartTime =
      typeof window !== "undefined" && typeof window.performance !== "undefined"
        ? window.performance.now()
        : Date.now();
  }

  /**
   * End timing page load and track the page view
   */
  endPageLoadTimingAndTrack(path: string, renderTime?: number): void {
    if (this.pageLoadStartTime === 0) {
      console.warn(
        "Page load timing not started. Call startPageLoadTiming() first."
      );
      return;
    }

    const loadTime =
      typeof window !== "undefined" && typeof window.performance !== "undefined"
        ? window.performance.now() - this.pageLoadStartTime
        : 0;
    this.trackPageView(path, loadTime, renderTime);
    this.pageLoadStartTime = 0;
  }

  /**
   * Set user properties in GA4 for audience segmentation.
   *
   * @param properties - Object with user property key-value pairs
   *
   * @example
   * analyticsV2.setUserProperties({
   *   country_code: "US",
   *   has_local_site: true,
   *   login_type: "sa",
   * });
   */
  setUserProperties(
    properties: Record<string, string | number | boolean>
  ): void {
    GoogleAnalyticsV2.setUserProperties(properties);

    if (this.debugMode || this.checkDebugMode()) {
      console.log(`📊 ANALYTICS: User Properties Set`);
      console.log(`📋 Properties:`, properties);
    }
  }

  /**
   * Get performance metrics for all tracked pages
   */
  getPerformanceData(): PagePerformanceData[] {
    return [...this.pagePerformanceData];
  }

  /**
   * Get average loading time for a specific path
   */
  getAverageLoadTime(path: string): number {
    const pathData = this.pagePerformanceData.filter(
      (data) => data.path === path
    );
    if (pathData.length === 0) return 0;

    const totalLoadTime = pathData.reduce(
      (sum, data) => sum + data.loadTime,
      0
    );
    return totalLoadTime / pathData.length;
  }

  /**
   * Get performance summary for all pages
   */
  getPerformanceSummary(): Record<
    string,
    { avgLoadTime: number; avgRenderTime: number; count: number }
  > {
    const summary: Record<
      string,
      { avgLoadTime: number; avgRenderTime: number; count: number }
    > = {};

    this.pagePerformanceData.forEach((data) => {
      if (!summary[data.path]) {
        summary[data.path] = { avgLoadTime: 0, avgRenderTime: 0, count: 0 };
      }

      summary[data.path].avgLoadTime += data.loadTime;
      summary[data.path].avgRenderTime += data.renderTime;
      summary[data.path].count++;
    });

    // Calculate averages
    Object.keys(summary).forEach((path) => {
      const { count } = summary[path];
      summary[path].avgLoadTime = Math.round(summary[path].avgLoadTime / count);
      summary[path].avgRenderTime = Math.round(
        summary[path].avgRenderTime / count
      );
    });

    return summary;
  }
}

// Export singleton instance
export const analyticsV2 = new AnalyticsServiceV2();

// Export types for external use
export type { PagePerformanceData };
